import { Box } from '@sequencehq/core-components'
import { Fragment, useEffect } from 'react'
import { Currency } from '@sequencehq/api'
import { usePrevious } from '@sequencehq/utils'
import {
  ConditionValueInputComponent,
  PERCENTAGE_UNIT
} from 'Automations/nodeDefinitions/conditions'
import { useDefaultCurrency } from 'SettingsV2/hooks/useCurrencies'
import { PriceUnitSelector } from './components/PriceUnitSelector'
import { PriceValueField } from './components/PriceValueField'

export type AmountWithUnitInputConfig = {
  /**
   * When set, adds `%` as a unit option. The function string can divide the
   * value by the percentage base property to compute a percentage at runtime.
   */
  allowPercentage?: boolean
  /**
   * When set, the unit dropdown lists every enabled account currency. Picking
   * one of these stores the currency code as the value's `unit`.
   */
  allowCurrency?: boolean
}

/**
 * The shape of `value` stored on the condition for amount-with-unit operators.
 * The amount is the user-entered numeric string; the unit is either a currency
 * code or `PERCENTAGE_UNIT`.
 */
export type AmountWithUnitValue = {
  amount: string
  unit: string
}

const isAmountWithUnitValue = (value: unknown): value is AmountWithUnitValue =>
  typeof value === 'object' && value !== null && 'amount' in value

const readAmount = (value: unknown): string =>
  isAmountWithUnitValue(value) ? value.amount : ((value || '') as string)

const readUnit = (value: unknown): string | undefined =>
  isAmountWithUnitValue(value) ? value.unit : undefined

/**
 * Factory that returns a `ConditionValueInputComponent` rendering a value field
 * alongside an inline unit selector. The component stores the value as
 * `{ amount, unit }` and delegates the unit dropdown and numeric input to the
 * shared `PriceUnitSelector` / `PriceValueField` components.
 */
export const amountWithUnitInput = (
  config: AmountWithUnitInputConfig
): ConditionValueInputComponent => {
  const Component: ConditionValueInputComponent = ({
    'data-testid': dataTestId,
    value,
    onChange,
    validationErrors
  }) => {
    const defaultCurrency = useDefaultCurrency()

    const amount = readAmount(value)
    const storedUnit = readUnit(value)
    const effectiveUnit = storedUnit ?? defaultUnitFor(config, defaultCurrency)

    // Backfill the condition's value with the resolved default unit when the
    // component mounts before a unit has been picked.
    const prevEffectiveUnit = usePrevious(effectiveUnit)
    useEffect(() => {
      if (effectiveUnit === prevEffectiveUnit) {
        return
      }

      if (!storedUnit && effectiveUnit) {
        onChange({ value: { amount, unit: effectiveUnit } })
      }
    }, [effectiveUnit, amount, onChange, storedUnit, prevEffectiveUnit])

    return (
      <Fragment>
        <Box gridArea="unit">
          <PriceUnitSelector
            data-testid={appendTestId(dataTestId, 'unit')}
            value={effectiveUnit ?? ''}
            onChange={unit => onChange({ value: { amount, unit } })}
            allowCurrency={config.allowCurrency}
            allowPercentage={config.allowPercentage}
          />
        </Box>
        <Box gridArea="value">
          <PriceValueField
            data-testid={appendTestId(dataTestId, 'value')}
            value={amount}
            unit={effectiveUnit}
            onChange={nextAmount =>
              onChange({
                value: { amount: nextAmount, unit: effectiveUnit ?? '' }
              })
            }
            validationErrors={validationErrors}
          />
        </Box>
      </Fragment>
    )
  }
  return Component
}

const defaultUnitFor = (
  config: AmountWithUnitInputConfig,
  defaultCurrency: Currency | undefined
): string | undefined => {
  if (config.allowCurrency && defaultCurrency) return defaultCurrency
  if (config.allowPercentage) return PERCENTAGE_UNIT
  return undefined
}

const appendTestId = (
  base: string | undefined,
  suffix: string
): string | undefined => (base ? `${base}.${suffix}` : undefined)
